import { Grid } from '@mui/material';
import { ODSCheckmark, ODSClose } from '@telekom-ods/react-icons';
import { ODSText, ODSTagStatic, ODSLoadingSpinner } from '@telekom-ods/react-ui-kit';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import Alert from '../../../../../components/Alert';
import Button from '../../../../../components/Buttons/Button';

import InfoRow from './InfoRow';
import './UpgradeCluster.css';
import ValidationGroup, { type ValidationGroupItem } from './ValidationGroup';

import type { UpgradeEligibilityResponse } from '@/types/caasTypes';

interface ValidationResultsProps {
  eligibility: UpgradeEligibilityResponse | null;
  isLoading?: boolean;
  error?: Error | null;
  canRunValidationCheck?: boolean;
  onRevalidate?: () => void;
}

const getArgoStatusType = (status?: string): 'success' | 'warning' => {
  if (status === 'Healthy') return 'success';
  return 'warning';
};

const ValidationResults = ({
  eligibility,
  isLoading = false,
  error,
  canRunValidationCheck = true,
  onRevalidate,
}: ValidationResultsProps): React.JSX.Element => {
  const intl = useIntl();
  const lastCheckNameRef = useRef<string | undefined>(undefined);

  const validation = eligibility?.validation;
  const checkName = eligibility?.checkName;

  if (checkName) {
    lastCheckNameRef.current = checkName;
  }

  const displayCheckName = checkName ?? lastCheckNameRef.current;
  const passed = eligibility?.eligibleForUpgrade === true;
  const clusterStatus = validation?.cluster?.status;
  const hasReroll = validation?.clusterReroll !== undefined;
  const rerollReady = validation?.clusterReroll?.success === true;
  const hasResults = eligibility?.validation !== undefined;

  const argoItems: ValidationGroupItem[] = (validation?.argoApps ?? []).map((app, idx) => ({
    key: app.name ?? String(idx),
    name: app.name ?? `App ${String(idx + 1)}`,
    tagLabel: app.currentStatus ?? 'Unknown',
    tagType: getArgoStatusType(app.currentStatus),
  }));
  const pdbItems: ValidationGroupItem[] = (validation?.pdbs ?? []).map((pdb, idx) => ({
    key: pdb.name ?? String(idx),
    name: pdb.name ?? `PDB ${String(idx + 1)}`,
    tagLabel: pdb.namespace ?? '—',
    tagType: 'warning',
  }));
  const podsNotRunningItems: ValidationGroupItem[] = (validation?.podsNotRunning ?? []).map(
    (pod, idx) => ({
      key: pod.name ?? String(idx),
      name: `${pod.namespace ?? '—'} / ${pod.name ?? `Pod ${String(idx + 1)}`}`,
      tagLabel: pod.status ?? 'Unknown',
      tagType: 'warning',
    })
  );
  const bitnamiItems: ValidationGroupItem[] = (validation?.podsWithBitnami ?? []).map(
    (pod, idx) => ({
      key: pod.podName ?? pod.name ?? String(idx),
      name: `${pod.namespace ?? '—'} / ${pod.podName ?? pod.name ?? `Pod ${String(idx + 1)}`}`,
      tagLabel: pod.image ?? 'Unknown',
      tagType: 'subtle',
    })
  );
  const machinePlanItems: ValidationGroupItem[] = (validation?.machinePlans ?? []).map(
    (plan, idx) => ({
      key: plan.resource ?? String(idx),
      name: plan.resource ?? `Plan ${String(idx + 1)}`,
      tagLabel: plan.state ?? 'unknown',
      tagType: 'subtle',
    })
  );

  const blockers: string[] = [];
  if (hasReroll && !rerollReady) blockers.push('Cluster reroll not ready');
  if (pdbItems.length > 0) {
    blockers.push(
      `${String(pdbItems.length)} PodDisruptionBudget${pdbItems.length > 1 ? 's' : ''} block a safe node drain`
    );
  }
  if (podsNotRunningItems.length > 0) {
    blockers.push(
      `${String(podsNotRunningItems.length)} pod${podsNotRunningItems.length > 1 ? 's' : ''} not running`
    );
  }
  const unhealthyArgo = argoItems.filter(a => a.tagType !== 'success').length;
  if (unhealthyArgo > 0) {
    blockers.push(`${String(unhealthyArgo)} ArgoCD app${unhealthyArgo > 1 ? 's' : ''} unhealthy`);
  }
  if (bitnamiItems.length > 0) {
    blockers.push(
      `${String(bitnamiItems.length)} pod${bitnamiItems.length > 1 ? 's' : ''} using Bitnami images`
    );
  }

  let statusTag: React.JSX.Element | null = null;
  if (isLoading) statusTag = <ODSLoadingSpinner size='small' />;
  else if (eligibility)
    statusTag = (
      <ODSTagStatic
        label={passed ? 'Passed' : 'Failed'}
        icon={passed ? <ODSCheckmark /> : <ODSClose />}
        type={passed ? 'success' : 'error'}
      />
    );
  else if (error)
    statusTag = (
      <ODSTagStatic
        label={intl.formatMessage({ id: 'error' })}
        type='error'
        icon={<ODSClose />}
      />
    );

  return (
    <details
      className='upgrade-acc'
      {...(!passed ? { open: true } : {})}
    >
      <summary className='upgrade-acc-sum'>
        <span
          className='upgrade-acc-chev'
          aria-hidden='true'
        >
          ›
        </span>
        <span className='upgrade-acc-title'>
          {intl.formatMessage({ id: 'preUpgradeValidation' })}
        </span>
        <span className='upgrade-acc-status'>{statusTag}</span>
      </summary>
      <div className='upgrade-acc-body'>
        {error && !eligibility ? (
          <Alert
            mode='error'
            title={intl.formatMessage({ id: 'failedToLoadValidationResults' })}
            text={error.message}
          />
        ) : (
          <Grid
            container
            direction='column'
            gap={1.5}
          >
            {!passed && blockers.length > 0 && (
              <Alert
                mode='error'
                title={intl.formatMessage({ id: 'clusterCannotBeUpgradedYet' })}
                text={blockers.join(' · ')}
              />
            )}

            {passed && (
              <ODSText
                as='p'
                className='p-text upgrade-text-muted'
              >
                Cluster status, ArgoCD apps and reroll all healthy
                {displayCheckName ? ` · Check ${displayCheckName}` : ''}
              </ODSText>
            )}

            {hasResults && (
              <>
                {clusterStatus && (
                  <InfoRow
                    label={intl.formatMessage({ id: 'clusterStatus' })}
                    value={clusterStatus}
                    tagType={clusterStatus === 'ReconciliationSucceeded' ? 'success' : 'warning'}
                  />
                )}
                {hasReroll && (
                  <InfoRow
                    label={intl.formatMessage({ id: 'clusterReroll' })}
                    value={rerollReady ? 'Ready' : 'Not Ready'}
                    tagType={rerollReady ? 'success' : 'warning'}
                  />
                )}
                <ValidationGroup
                  title={intl.formatMessage({ id: 'machinePlans' })}
                  items={machinePlanItems}
                />
                <ValidationGroup
                  title={intl.formatMessage({ id: 'pdbs' })}
                  items={pdbItems}
                />
                <ValidationGroup
                  title={intl.formatMessage({ id: 'podsNotRunning' })}
                  items={podsNotRunningItems}
                />
                <ValidationGroup
                  title={intl.formatMessage({ id: 'podsWithBitnamiImages' })}
                  items={bitnamiItems}
                />
                <ValidationGroup
                  title={intl.formatMessage({ id: 'argoCdApplications' })}
                  items={argoItems}
                />
              </>
            )}

            {onRevalidate && (
              <Grid
                item
                sx={{ width: 'fit-content' }}
              >
                <Button
                  variant='outline'
                  size='small'
                  label={intl.formatMessage({ id: 'rerunValidation' })}
                  disabled={isLoading || !canRunValidationCheck}
                  onClick={onRevalidate}
                />
              </Grid>
            )}
          </Grid>
        )}
      </div>
    </details>
  );
};

export default ValidationResults;
